// Story 7.1 (ADO-904), AC1/AC2/AC6 — the product listing for a selected node
// (Figma "Category Listing"). Renders the breadcrumb, the filters rail, the
// product grid, and — for an empty node — an explicit empty state (AC6), never
// an error. Epic 7 pixel parity (ADO-903): geometry, toolbar, pagination footer
// and mobile filters drawer ported from the prototype category-listing page.

import { useEffect, useRef, useState } from 'react';
import {
  Banner,
  Button,
  CollapsibleFilterSidebar,
  Icon,
  Pagination,
  Toggle,
} from '../../design-system';
import { FiltersDrawer } from './FiltersDrawer';
import { useViewMode } from './useViewMode';
import {
  type CategoryNode,
  fetchChildren,
  fetchNodeProducts,
  hasProductScope,
  NO_PRODUCT_SCOPE,
  type NodeListing,
  type NodeListingPage,
  type ProductScope,
  type ProductSummary,
} from '../../api/browse.client';
import {
  hasActiveFilters,
  removeFilterValue,
  toggleFilterValue,
  type FacetKey,
  type ProductFilters,
} from '../../api/facets';
import { useFilterPanel } from '../../api/useFilterPanel';
import { useAvailability } from '../../availability/useAvailability';
import { usePrices } from '../../pricing/usePriceStore';
import { useOnOrder } from '../../onOrder/useOnOrder';
import { useFavouritesStore } from '../../favourites/useFavouritesStore';
import { useCatalogueEntryKey } from '../../ordering/catalogueEntry';
import { formatCategoryName } from './categoryDisplay';
import { Breadcrumb } from './Breadcrumb';
import { FilterChips, type DrillChip } from './FilterChips';
import { FilterPanel } from './FilterPanel';
import { ProductCard } from './ProductCard';
import styles from './CategoryListing.module.css';

export interface CategoryListingProps {
  /** The selected node whose products to list; null = the ROOT (all products). */
  readonly node: CategoryNode | null;
  /** Navigate to a breadcrumb crumb (`null` = Shop root, clears the selection). */
  readonly onNavigate: (node: CategoryNode | null) => void;
  /** Epic 7 demo polish (2026-07-17) — the prototype's `?view=favourites` mode:
   *  the SAME listing shell (rail, toolbar, grid/list toggle, cards) but the
   *  product source is the favourites store (no catalogue fetch), the breadcrumb
   *  becomes a "Favourites" heading + "View all" link, chips/pagination hide,
   *  and the empty state is the prototype's heart prompt. */
  readonly favouritesView?: boolean;
  /**
   * Story 8.1 (ADO-2010), Tasks 8.2/8.3/8.4 — the landing-tile scope from the URL (a
   * tile's categories, an LSN category, Catering only). It ANDs with the node and the
   * facets, and each part is a removable chip (A10.4: the Catering default can be
   * dropped to see every product at the depot). Ignored in favourites view.
   */
  readonly scope?: ProductScope;
  /** The name a hierarchy scope's chip shows (the landing tile's name). */
  readonly scopeLabel?: string | undefined;
  /** Replace the scope (the host owns it, in the URL). */
  readonly onScopeChange?: (next: ProductScope) => void;
  /** Offer the "Catering only" switch: Foodservice accounts only (A10.4). */
  readonly offerCateringOnly?: boolean;
}

type Phase = 'idle' | 'loading' | 'error' | 'ready';

// Story 7-FU-D (ADO-1217), AC3 — the first visual row of cards is the LCP
// candidate, so mark the first N as `priority` (eager + fetchpriority=high). N is
// a responsive-grid judgement call (D-3): a small constant ≈ one row on the
// widest layout; over-eagering a few extra cards is harmless, under-eagering
// defeats the LCP fix. True LCP timing is a Playwright/manual concern.
const EAGER_IMAGE_COUNT = 4;

// Story 7-FU-C (ADO-1216) — the functional "Per page" segmented control. These
// values are lock-step with the backend `BROWSE_PAGE_SIZE_OPTIONS` allow-list
// (the server rejects a `?pageSize=` outside this set); `DEFAULT_PER_PAGE` mirrors
// the backend `DEFAULT_BROWSE_PAGE_SIZE`, i.e. the size the control shows selected
// on first load.
const PER_PAGE_OPTIONS = [10, 20, 50, 100] as const;
const DEFAULT_PER_PAGE = 20;

/**
 * Enabler E7-C (ADO-2274), D-1: the listing is one ordering entry's range, so its state (grid,
 * page, filters, departments, a page error) belongs to that entry. The body is keyed by the entry
 * and starts afresh whenever it changes, rather than each piece of state being reset by hand.
 * With no entry it waits in its loading state and asks nothing.
 */
export function CategoryListing(props: CategoryListingProps) {
  const catalogueEntryKey = useCatalogueEntryKey();
  return (
    <CategoryListingForEntry
      key={catalogueEntryKey ?? 'no-entry'}
      {...props}
      catalogueEntryKey={catalogueEntryKey}
    />
  );
}

function CategoryListingForEntry({
  node,
  onNavigate,
  favouritesView = false,
  scope = NO_PRODUCT_SCOPE,
  scopeLabel,
  onScopeChange,
  offerCateringOnly = false,
  catalogueEntryKey,
}: CategoryListingProps & { readonly catalogueEntryKey: string | null }) {
  // Story 7.10 (ADO-906): the active attribute filters (AC1/AC3). Reset when the
  // node changes (scoped to their node). `showFilters` toggles the pinned rail on
  // desktop (Filters Switch, Figma 531:1752); `drawerOpen` drives the <1024px
  // filters drawer (prototype mobile Drawer).
  const [filters, setFilters] = useState<ProductFilters>({});
  const { showFilters, toggleFilters } = useFilterPanel();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // The last successfully loaded listing for the current node/page — the anchor
  // for the breadcrumb, node header, facet panel, and the pagination envelope.
  // Retained across a page/filter refetch so the current grid stays on screen
  // while the next page is in flight (no blank flash on Previous/Next).
  const [listing, setListing] = useState<NodeListing | null>(null);
  // Story 7-FU-C (ADO-1216), AC2 — the products of the CURRENT page (each page
  // REPLACES the previous, offset pagination — not the earlier accumulate-on-Next
  // load-more), plus the pagination envelope (`page`/`pageSize`/`total`/`totalPages`).
  const [products, setProducts] = useState<readonly ProductSummary[]>([]);
  const [page, setPage] = useState<NodeListingPage | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  // Story 7-FU-C (ADO-1216), AC2 — the SINGLE request intent: the page AND the
  // "Per page" size the next fetch will use, plus a `nonce` so a retry of the SAME
  // page/size (its nonce bumped) still re-fires the fetch effect. Page size lives
  // HERE (not in a separate `pageSize` state) on purpose: what the footer DISPLAYS
  // (indicator, "Per page" highlight, Previous/Next enablement) is read from the
  // committed `page` envelope below — never this request-side intent — and folding
  // size into the intent means a failed "Per page" change can't strand a size that
  // the footer, a later Previous/Next, or a later filter change would then apply
  // behind the user's back (PR #252 review, r3). Selecting a size, changing a
  // filter, and paging all write a fresh intent; the committed envelope only moves
  // on a successful load.
  const [fetchReq, setFetchReq] = useState<{ page: number; pageSize: number; nonce: number }>({
    page: 1,
    pageSize: DEFAULT_PER_PAGE,
    nonce: 0,
  });
  // A transient page-navigation failure: the current grid stays put and a retry
  // notice appears, so a failed Previous/Next never blanks what the user is viewing.
  const [pageError, setPageError] = useState(false);
  // Monotonic fetch generation. A settlement whose generation is no longer current
  // (a newer node/filter/page fetch started) is stale and dropped — it must never
  // paint results onto the wrong node/page.
  const fetchSeqRef = useRef(0);
  // Mirrors `listing` for the fetch effect's catch, WITHOUT making the effect
  // depend on `listing` (which would refetch on every settlement). Lets a failed
  // fetch tell a FIRST load (nothing on screen → error state) from a page
  // NAVIGATION (grid present → keep it + show the retry notice).
  const listingRef = useRef<NodeListing | null>(listing);
  listingRef.current = listing;

  // The applied "Per page" size — read from the committed envelope (moves only on a
  // successful load), never the request-side intent. The single source for the
  // footer highlight AND for the size a page/filter change keeps, so a failed size
  // change can't leave the two disagreeing (PR #252 review).
  const committedPageSize = page?.pageSize ?? DEFAULT_PER_PAGE;

  // Request a page at the CURRENTLY APPLIED size: bump the nonce so even a same-page
  // request re-fires the effect, and clear any prior page error. Previous/Next and
  // the over-range recovery route through here; a size change goes through
  // `selectPageSize`, a filter/node change through the reset block below.
  const requestPage = (target: number) => {
    setPageError(false);
    setFetchReq((r) => ({
      page: Math.max(1, target),
      pageSize: committedPageSize,
      nonce: r.nonce + 1,
    }));
  };
  // Retry the LAST request verbatim (same page AND size, nonce bumped) — the retry
  // notice's "Try again" re-attempts exactly what failed, whether that was a
  // Previous/Next, a "Per page" change, or a filter change.
  const retryFetch = () => {
    setPageError(false);
    setFetchReq((r) => ({ ...r, nonce: r.nonce + 1 }));
  };
  // Epic 7 re-skin (ADO-903): grid/list view toggle, opening in the grid view at
  // every width (shared with ProductSearch).
  const { viewMode, selectView } = useViewMode();
  // The full Department list + the current Department's categories power the
  // sidebar Department/Category sections (real taxonomy navigation). Fetched
  // lazily via the same hierarchy client the mega-menu uses.
  const [departments, setDepartments] = useState<readonly CategoryNode[]>([]);
  const [categories, setCategories] = useState<readonly CategoryNode[]>([]);

  const nodeId = node?.id ?? null;

  // Reset filter state synchronously when the selected node changes — filters are
  // scoped to their node and must not leak across a Department/Category switch.
  // Doing it during render (not in an effect) means the fetch effect below never
  // fires a transient request with the previous node's stale filters (PR #74 review).
  const [renderedNodeId, setRenderedNodeId] = useState(nodeId);
  if (nodeId !== renderedNodeId) {
    setRenderedNodeId(nodeId);
    setFilters({});
    setListing(null);
    // Story 7-FU-C (ADO-1216), AC2 — a node switch blanks the grid + pagination
    // so nothing leaks across nodes, and starts a fresh page 1 (the explicit
    // `setFetchReq` below fires the fetch even when `filters` are unchanged).
    setProducts([]);
    setPage(null);
    setPageError(false);
    // A node switch always starts at page 1 and re-fires the fetch — even when the
    // new node's filters (hence `resetKey`) are unchanged (e.g. ROOT → a node both
    // with no filters), so the `resetKey` block below would not fire. The applied
    // "Per page" size carries across the switch (a user preference, not node state).
    setFetchReq((r) => ({ page: 1, pageSize: committedPageSize, nonce: r.nonce + 1 }));
    // Epic 7 demo polish (2026-07-17): `null` is the ROOT (all-products) listing
    // now — a real fetch, not an idle prompt.
    setPhase('loading');
  }

  // Story 7-FU-C (ADO-1216), AC2 — a FILTER change RESETS to page 1 (an offset into
  // a now-different result set is meaningless), keeping the applied "Per page" size.
  // Keyed on the filters alone — a page-SIZE change is handled by `selectPageSize`
  // (which writes its own intent), so it must NOT ride this key: keying on a
  // request-side size would let a failed size change strand the key and silently
  // refetch a later filter change at the size that never loaded (PR #252 review, r3).
  // The reset keeps `committedPageSize` (the last SUCCESSFUL size), so the applied
  // size always matches the footer. `fetchReq`'s bumped nonce re-fires the fetch
  // effect. Multiple intermediate resets in one commit collapse to a single effect
  // run with the final filters (effects run after commit, not per render pass).
  const filtersKey = JSON.stringify(filters);
  // Story 8.1 (ADO-2010): a scope change is a filter change too (page 1, same size).
  const resetKey = `${filtersKey}|${JSON.stringify(scope)}`;
  const [renderedResetKey, setRenderedResetKey] = useState(resetKey);
  if (resetKey !== renderedResetKey) {
    setRenderedResetKey(resetKey);
    setPageError(false);
    setFetchReq((r) => ({ page: 1, pageSize: committedPageSize, nonce: r.nonce + 1 }));
  }

  // Story 7-FU-C (ADO-1216), AC2 — the page fetch. Each settled `fetchReq`
  // (a node/filter/pageSize reset, a Previous/Next, or a retry) loads exactly that
  // page and REPLACES the grid. `fetchSeqRef` makes any in-flight fetch stale once
  // a newer one starts, so a slow page can never paint over a fresher one.
  // Favourites view — the product source is the store, not the catalogue fetch.
  const favourites = useFavouritesStore((s) => s.favourites);
  const favouritesHydrated = useFavouritesStore((s) => s.hydrated);
  const favouritesError = useFavouritesStore((s) => s.error);
  const hydrateFavourites = useFavouritesStore((s) => s.hydrate);
  useEffect(() => {
    if (favouritesView) void hydrateFavourites();
  }, [favouritesView, hydrateFavourites]);

  useEffect(() => {
    if (favouritesView) return; // favourites come from the store, never fetched here
    const seq = ++fetchSeqRef.current;
    setPhase('loading');
    // With no entry there is nothing to ask: the page waits (CategoryListing keys this body by the
    // entry, so it starts empty and a chosen entry mounts a fresh one).
    if (catalogueEntryKey === null) return;
    fetchNodeProducts(
      catalogueEntryKey,
      nodeId,
      undefined,
      filters,
      fetchReq.page,
      fetchReq.pageSize,
      scope,
    )
      .then((next) => {
        if (seq !== fetchSeqRef.current) return;
        setListing(next);
        setProducts(next.products);
        // Commit the loaded page envelope — the single source of truth for the
        // footer (indicator, "Per page" highlight, Previous/Next). The server
        // echoes the requested page; a failed navigation leaves this untouched, so
        // the footer keeps matching the grid still on screen.
        setPage(next.page);
        setPageError(false);
        setPhase('ready');
      })
      .catch(() => {
        if (seq !== fetchSeqRef.current) return;
        if (listingRef.current === null) {
          // FIRST load with nothing on screen — surface a clean error state.
          setListing(null);
          setProducts([]);
          setPage(null);
          setPhase('error');
        } else {
          // A page NAVIGATION failed — keep the current grid (and the committed
          // `page` envelope that drives the footer, left untouched) and show a
          // retry notice; Previous/Next/Try-again re-request from here.
          setPhase('ready');
          setPageError(true);
        }
      });
    // A newer effect run bumps `fetchSeqRef`, which supersedes this settlement.
  }, [favouritesView, nodeId, filters, fetchReq, scope, catalogueEntryKey]);

  // The products actually on screen: the favourites list in favourites view, else
  // the current catalogue page. FavouriteProduct is a structural superset of
  // ProductSummary (plus `accountTypeEligible` for AC4b gating).
  const displayProducts = favouritesView ? favourites : products;

  // Story 7-FU-C (ADO-1216), AC2 — derived pagination state for the footer. Every
  // value the footer DISPLAYS is anchored to the committed `page` envelope (set
  // only on a successful load), never the request-side `fetchReq` intent. So a
  // failed filter/"Per page" change (grid held on screen, PR #252 review) can't
  // leave the indicator, the "Per page" highlight, or Previous/Next reporting a
  // page/size that never loaded — they stay in step with the grid on screen.
  const totalPages = page?.totalPages ?? 1;
  const total = page?.total ?? 0;
  const committedPage = page?.page ?? 1;
  // `committedPageSize` is derived once, near the request helpers above (the single
  // source shared by the footer highlight and the size a page/filter change keeps).
  // A page navigation is in flight while the grid stays on screen — disable the
  // pager so a double-click can't stack requests or skip a page.
  const navigating = phase === 'loading' && listing !== null;
  // Story 7-FU-C review (PR #252) — an empty window while `total > 0`: an offset
  // past the data. The backend now clamps an over-range page to the LAST page, so
  // this is only reached when the set shrank between the `count` and the row read
  // (a data race) — a recoverable state, NOT an error and NOT the `total === 0`
  // empty state. Only meaningful once a page has settled (`!navigating`); mid-flight
  // the previous page's grid is still held on screen.
  const overRange =
    !favouritesView && listing !== null && total > 0 && displayProducts.length === 0 && !navigating;
  // Story 7-FU-C review (PR #252) — the "Showing X–Y of Z" caption is anchored to
  // the COMMITTED page envelope (`page`, set only on a successful load), never the
  // request-side `fetchReq` intent which changes the instant a Previous/Next/
  // "Per page" is requested. During a navigation the grid still shows the previous page, so
  // anchoring here keeps the caption consistent with what's on screen (no transient
  // "Showing 1–20" over a page-3 grid) and — because the range uses the real
  // on-screen count — it can never invert (the old `total>0 && length===0` case).
  const rangeStart = page !== null ? (page.page - 1) * page.pageSize + 1 : 0;
  const rangeEnd = page !== null ? (page.page - 1) * page.pageSize + displayProducts.length : 0;
  const canPrev = committedPage > 1 && !navigating;
  const canNext = committedPage < totalPages && !navigating;
  const goPrev = () => {
    if (!canPrev) return;
    requestPage(committedPage - 1);
  };
  const goNext = () => {
    if (!canNext) return;
    requestPage(committedPage + 1);
  };
  // Per-page: request page 1 at the new size. The guard compares against the
  // COMMITTED (applied) size, not the request-side intent — so re-selecting the
  // size the footer shows active is a true no-op, while re-selecting a size whose
  // fetch just FAILED (committed size unchanged) correctly retries it rather than
  // being swallowed as "no change" (PR #252 review, r3).
  const selectPageSize = (n: number) => {
    if (n === committedPageSize) return;
    setPageError(false);
    setFetchReq((r) => ({ page: 1, pageSize: n, nonce: r.nonce + 1 }));
  };

  // Story 7.3 (ADO-1139): populate the availability store (stock pill +
  // "{n} in store") for the products on screen; no-op when unconfigured.
  useAvailability(displayProducts.map((p) => p.sku));
  // Pricing migration 17 (ADO-2248): one price request for the same products (no-op unless
  // VITE_REAL_PRICES_ENABLED is on).
  usePrices(displayProducts.map((p) => p.sku));
  // Story 8.12 (ADO-2021): which of them the Customer already has on order, one batched read.
  useOnOrder(displayProducts.map((p) => p.sku));

  // Epic 7 re-skin (ADO-903) — the Department/Category the current listing sits
  // under (breadcrumb is root-first: Department → Category → Sub-Category).
  const currentDepartment = listing?.breadcrumb.find((n) => n.level === 'DEPARTMENT') ?? null;
  const currentCategory = listing?.breadcrumb.find((n) => n.level === 'CATEGORY') ?? null;
  const currentDepartmentId = currentDepartment?.id ?? null;

  // Load the Department list once — the sidebar Department radio. A failure just
  // leaves the section unrendered (the mega-menu remains the primary nav).
  useEffect(() => {
    if (catalogueEntryKey === null) return;
    let cancelled = false;
    fetchChildren(catalogueEntryKey, null)
      .then((res) => {
        if (!cancelled) setDepartments(res.children);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [catalogueEntryKey]);

  // Load the current Department's categories for the sidebar Category section.
  useEffect(() => {
    if (currentDepartmentId === null || catalogueEntryKey === null) {
      setCategories([]);
      return;
    }
    let cancelled = false;
    fetchChildren(catalogueEntryKey, currentDepartmentId)
      .then((res) => {
        if (!cancelled) setCategories(res.children);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [currentDepartmentId, catalogueEntryKey]);

  // A refetch (e.g. a filter change) keeps the loaded grid on screen; the spinner
  // only shows on the initial load of a node, when there's nothing to hold (PR #74).
  const scopeActive = !favouritesView && hasProductScope(scope);
  const filtersActive = hasActiveFilters(filters) || scopeActive;
  const facets = listing?.facets ?? [];

  // Story 7.10 — attribute-filter handlers (all produce a NEW filters object →
  // the fetch effect refetches). Clearing restores the unfiltered listing (AC4).
  const toggleValue = (key: FacetKey, value: string) =>
    setFilters((f) => toggleFilterValue(f, key, value));
  // Clear resets the attribute filters AND the taxonomy drill, restoring the
  // unfiltered ROOT (all-products) listing — with no default Department, the
  // Department/Category selections read as filters and "Clear" resets them too
  // (2026-07-17 demo feedback; matches the prototype's Clear).
  const clearAll = () => {
    setFilters({});
    if (scopeActive) onScopeChange?.(NO_PRODUCT_SCOPE);
    if (node !== null) onNavigate(null);
  };
  // Story 8.1 (ADO-2010): one removable chip per scope part.
  const withoutScope = (key: keyof ProductScope): ProductScope => {
    const next: { -readonly [K in keyof ProductScope]: ProductScope[K] } = { ...scope };
    delete next[key];
    return next;
  };
  const scopeChips: DrillChip[] = favouritesView
    ? []
    : [
        ...(scope.hierarchyCode !== undefined
          ? [
              {
                label: scopeLabel ?? 'Selected categories',
                onRemove: () => onScopeChange?.(withoutScope('hierarchyCode')),
              },
            ]
          : []),
        ...(scope.lsnCategory !== undefined
          ? [
              {
                label: scope.lsnCategory,
                onRemove: () => onScopeChange?.(withoutScope('lsnCategory')),
              },
            ]
          : []),
        ...(scope.globalFavourite !== undefined
          ? [
              {
                label: scopeLabel ?? scope.globalFavourite,
                onRemove: () => onScopeChange?.(withoutScope('globalFavourite')),
              },
            ]
          : []),
        ...(scope.cateringOnly === true
          ? [
              {
                label: 'Catering only',
                onRemove: () => onScopeChange?.(withoutScope('cateringOnly')),
              },
            ]
          : []),
      ];
  const setCateringOnly = (on: boolean) =>
    onScopeChange?.(on ? { ...scope, cateringOnly: true } : withoutScope('cateringOnly'));
  const removeChip = (key: FacetKey, value: string) =>
    setFilters((f) => removeFilterValue(f, key, value));
  // Epic 7 demo polish (2026-07-17) — the drill selection renders as a leading
  // blue chip (prototype ActiveFilterChips: department/category chips). Removing
  // it returns to the root listing.
  const drillChip =
    node !== null
      ? { label: formatCategoryName(node.name), onRemove: () => onNavigate(null) }
      : undefined;

  const filterPanel = (
    <FilterPanel
      facets={facets}
      selected={filters}
      onToggleValue={toggleValue}
      onClearAll={clearAll}
      hasActive={filtersActive}
      departments={departments}
      categories={categories}
      currentDepartmentId={currentDepartmentId}
      currentCategoryId={currentCategory?.id ?? null}
      onNavigate={onNavigate}
    />
  );

  return (
    <section className={styles.listing} aria-label="Product listing">
      {/* Prototype layout: a pinned filters rail on the LEFT (CollapsibleFilterSidebar,
          desktop only — flush to the viewport edge below the header) and the listing
          content on the RIGHT (toolbar + chips + grid + pagination footer). */}
      <div className={styles.shell}>
        <CollapsibleFilterSidebar open={showFilters} className={styles.sidebar} showAdPlaceholder>
          <div id="category-filter-panel">{filterPanel}</div>
        </CollapsibleFilterSidebar>

        <div className={styles.content}>
          {/* Toolbar (prototype "Listing options" row): Filters switch (desktop) /
              Filters drawer button (<1024) on the left · breadcrumb in the middle ·
              result count + grid/list view toggle on the right. */}
          <div className={styles.toolbar} role="group" aria-label="Listing options">
            <button
              type="button"
              className={styles.filtersToggle}
              aria-expanded={showFilters}
              aria-controls="category-filter-panel"
              onClick={toggleFilters}
            >
              Filters
              <span
                className={`${styles.filtersSwitch} ${showFilters ? styles.filtersSwitchOn : ''}`}
                aria-hidden
              >
                <span className={styles.filtersKnob} />
              </span>
            </button>
            <button
              type="button"
              className={styles.drawerButton}
              aria-haspopup="dialog"
              onClick={() => setDrawerOpen(true)}
            >
              <Icon name="sliders" size={20} />
              Filters
            </button>

            <div className={styles.toolbarCrumb}>
              {favouritesView ? (
                // Prototype favourites view: heading + "View all" link replace
                // the breadcrumb.
                <div className={styles.favouritesHeadingRow}>
                  <h2 className={styles.favouritesHeading}>Favourites</h2>
                  <button
                    type="button"
                    className={styles.favouritesViewAll}
                    onClick={() => onNavigate(null)}
                  >
                    View all
                  </button>
                </div>
              ) : (
                <Breadcrumb
                  trail={listing?.breadcrumb ?? (node !== null ? [node] : [])}
                  onNavigate={onNavigate}
                />
              )}
            </div>

            <div className={styles.toolbarRight}>
              {(favouritesView ? favouritesHydrated : listing !== null) && (
                <p className={styles.count} role="status">
                  {favouritesView
                    ? // Favourites are a single client-side list (no pagination).
                      displayProducts.length === 0
                      ? 'No products'
                      : `Showing ${displayProducts.length} ${displayProducts.length === 1 ? 'product' : 'products'}`
                    : // Story 7-FU-C (ADO-1216): the current page's window into the
                      // full result set — "Showing 21–40 of 137 products". Anchored
                      // to the committed envelope (`rangeStart`/`rangeEnd`) so it
                      // can't invert or flash a stale range during navigation.
                      total === 0
                      ? 'No products'
                      : displayProducts.length === 0
                        ? // Empty window with total > 0 (the over-range data race) —
                          // report the count without a range rather than "0–20".
                          `${total} ${total === 1 ? 'product' : 'products'}`
                        : `Showing ${rangeStart}–${rangeEnd} of ${total} ${total === 1 ? 'product' : 'products'}`}
                </p>
              )}
              <div className={styles.viewToggle} role="group" aria-label="View mode">
                <button
                  type="button"
                  className={`${styles.viewButton} ${viewMode === 'grid' ? styles.viewButtonActive : ''}`}
                  aria-label="Grid view"
                  aria-pressed={viewMode === 'grid'}
                  onClick={() => selectView('grid')}
                >
                  <Icon name="gridView" size={20} />
                </button>
                <button
                  type="button"
                  className={`${styles.viewButton} ${viewMode === 'list' ? styles.viewButtonActive : ''}`}
                  aria-label="List view"
                  aria-pressed={viewMode === 'list'}
                  onClick={() => selectView('list')}
                >
                  <Icon name="listView" size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* Chips are hidden in favourites view (prototype parity). */}
          {!favouritesView && (
            <FilterChips
              filters={filters}
              onRemove={removeChip}
              onClearAll={clearAll}
              drillChip={drillChip}
              scopeChips={scopeChips}
              className={styles.chipsRow}
            />
          )}
          {/* Story 8.1 (ADO-2010), A10.4 — Catering only, offered to Foodservice only. */}
          {!favouritesView && offerCateringOnly && onScopeChange !== undefined && (
            <div className={styles.cateringRow}>
              <Toggle
                label="Catering only"
                checked={scope.cateringOnly === true}
                onChange={setCateringOnly}
              />
            </div>
          )}

          <div className={styles.main}>
            {favouritesView && !favouritesHydrated && !favouritesError && (
              <p className={styles.status} role="status">
                Loading your favourites…
              </p>
            )}
            {favouritesView && favouritesError && (
              <Banner type="error">
                We couldn&rsquo;t load your favourites.{' '}
                <button
                  type="button"
                  className={styles.favouritesRetry}
                  onClick={() => void hydrateFavourites()}
                >
                  Try again
                </button>
              </Banner>
            )}
            {favouritesView && favouritesHydrated && favourites.length === 0 && (
              // Prototype favourites empty state: heart circle + prompt + link.
              <div className={styles.empty} role="status">
                <span className={styles.emptyHeart} aria-hidden="true">
                  <Icon name="heart" size={28} />
                </span>
                <p className={styles.emptyTitle}>Tap the heart on any product to save it here</p>
                <button
                  type="button"
                  className={styles.favouritesViewAll}
                  onClick={() => onNavigate(null)}
                >
                  Browse all products
                </button>
              </div>
            )}

            {!favouritesView && phase === 'loading' && listing === null && (
              <p className={styles.status} role="status">
                Loading products…
              </p>
            )}

            {!favouritesView && phase === 'error' && (
              <Banner type="error">Couldn&rsquo;t load products. Please try again.</Banner>
            )}

            {!favouritesView && listing !== null && total === 0 && (
              // AC6 / AC4 — explicit empty state, not an error. Shown when the node
              // (under the active filters) has NO products at all — `total === 0`,
              // so there are no pages to navigate to either. When attribute filters
              // are active, offer a clear-filters affordance to recover.
              <div className={styles.empty} role="status">
                <p className={styles.emptyTitle}>
                  {filtersActive
                    ? `No products in ${node !== null ? formatCategoryName(node.name) : 'the catalogue'} match the selected filters.`
                    : `There are no products in ${node !== null ? formatCategoryName(node.name) : 'the catalogue'} yet.`}
                </p>
                <p className={styles.emptyHint}>
                  {filtersActive
                    ? 'Try removing a filter to widen your results.'
                    : 'Try another category, or check back once the range is updated.'}
                </p>
                {filtersActive && (
                  <Button variant="secondary" onClick={clearAll}>
                    Clear filters
                  </Button>
                )}
              </div>
            )}

            {overRange && (
              // Story 7-FU-C review (PR #252) — an empty window while `total > 0`.
              // Distinct from `total === 0` (a genuinely empty node) and from a load
              // error: the results changed under the current page, so recover to the
              // first page rather than leaving the grid, empty-state AND error all
              // unrendered (which used to leave just a bare footer).
              <div className={styles.empty} role="status">
                <p className={styles.emptyTitle}>That page is no longer available.</p>
                <p className={styles.emptyHint}>The results changed while you were browsing.</p>
                <Button variant="secondary" onClick={() => requestPage(1)}>
                  Back to the first page
                </Button>
              </div>
            )}

            {displayProducts.length > 0 && (
              <ul className={viewMode === 'list' ? styles.list : styles.grid}>
                {displayProducts.map((product, index) => (
                  <li key={product.id}>
                    <ProductCard
                      product={product}
                      accountTypeEligible={
                        'accountTypeEligible' in product ? product.accountTypeEligible : undefined
                      }
                      priority={index < EAGER_IMAGE_COUNT}
                      layout={viewMode}
                    />
                  </li>
                ))}
              </ul>
            )}

            {/* Story 7-FU-C (ADO-1216), AC2 — the FUNCTIONAL pagination footer.
                "Per page" picks the page size (resets to page 1); "Previous"/"Next"
                step through the numbered pages (disabled at the ends and while a
                page is loading); "Page x of y" reads from the server `total`. A
                failed navigation keeps the grid + shows the retry notice below.
                Hidden while `overRange` (an empty window past the data) so the sole
                way out of a dead page is the "Back to the first page" recovery
                above, not a second one via a still-live pager (PR #252 review, r3). */}
            {!favouritesView && listing !== null && total > 0 && !overRange && (
              <Pagination
                page={committedPage}
                totalPages={totalPages}
                pageSize={committedPageSize}
                pageSizeOptions={PER_PAGE_OPTIONS}
                onPageChange={(next) => (next < committedPage ? goPrev() : goNext())}
                onPageSizeChange={selectPageSize}
                busy={navigating}
                idPrefix="catalogue"
              />
            )}

            {pageError && (
              <p className={styles.loadMoreHint} role="status">
                Couldn&rsquo;t load that page.{' '}
                <button type="button" className={styles.favouritesRetry} onClick={retryFetch}>
                  Try again
                </button>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Mobile/tablet (<1024px) filters drawer — the SAME panel, in the shared
          left drawer with a sticky "Apply filters" footer (filtering is already
          live; the button just closes the drawer, prototype parity). */}
      <FiltersDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        {filterPanel}
      </FiltersDrawer>
    </section>
  );
}
